import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { GraduationCap, X, ChevronRight } from "lucide-react";
import { api } from "../lib/api";
import { logger } from "../lib/logger";
import { useCountdown, pad2 } from "../hooks/useCountdown";
import { PromoVideoWidget } from "./courses/PromoVideoWidget";

const DISMISS_KEY = "fv_courses_widget_dismissed";

const Countdown = ({ start }) => {
  const cd = useCountdown(start);
  if (!cd) return null;
  const parts = cd.days > 0 ? [[cd.days, "j"], [cd.hours, "h"], [cd.minutes, "min"]] : [[cd.hours, "h"], [cd.minutes, "min"], [cd.seconds, "s"]];
  return (
    <span className="font-mono tabular-nums text-white whitespace-nowrap shrink-0" data-testid="courses-widget-countdown">
      {parts.map(([v, u]) => `${pad2(v)}${u}`).join(" ")}
    </span>
  );
};

/** Floating promo card (bottom-right) for the Saison 2026-2027 courses — dismissible per session. */
export const CoursesFloatingWidget = () => {
  const [season, setSeason] = useState(null);
  const [dismissed, setDismissed] = useState(() => !!sessionStorage.getItem(DISMISS_KEY));
  const [expanded, setExpanded] = useState(true);

  useEffect(() => {
    api.get("/courses/season").then(r => setSeason(r.data)).catch(err => logger.warn("[courses] widget failed:", err));
  }, []);

  if (dismissed || !season) return null;
  const next = season.next_session;
  const dismiss = () => { sessionStorage.setItem(DISMISS_KEY, "1"); setDismissed(true); };

  if (!expanded) {
    return (
      <button type="button" onClick={() => setExpanded(true)} data-testid="courses-widget-collapsed" className="fixed bottom-5 right-5 z-[60] inline-flex items-center gap-2 px-4 py-3 rounded-full bg-[color:var(--accent)] text-black font-semibold text-xs uppercase tracking-widest shadow-[0_20px_50px_-15px_rgba(240,180,41,0.6)] hover:brightness-110 transition animate-[fadeUp_.4s_ease-out]">
        <GraduationCap className="w-4 h-4" /> Cours {season.label.replace("Saison ", "")}
      </button>
    );
  }

  return (
    <aside className="fixed bottom-5 right-5 z-[60] w-[min(92vw,340px)] rounded-2xl border border-[color:var(--accent)]/40 bg-[color:var(--bg-elev)]/95 backdrop-blur-xl shadow-[0_30px_80px_-20px_rgba(0,0,0,0.8)] overflow-hidden animate-[fadeUp_.5s_ease-out]" data-testid="courses-widget" aria-label="Cours Saison 2026-2027">
      <div className="h-1 w-full" style={{ background: `linear-gradient(90deg, ${next?.color || "var(--accent)"}, var(--accent))` }} />
      <div className="p-4">
        <div className="flex items-start gap-3">
          <span className="w-10 h-10 rounded-xl bg-[color:var(--accent)]/15 border border-[color:var(--accent)]/40 grid place-items-center shrink-0">
            <GraduationCap className="w-5 h-5 text-[color:var(--accent)]" />
          </span>
          <div className="min-w-0 flex-1">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[color:var(--accent)]">{season.label} · {season.venue_name}</div>
            <div className="font-display text-2xl leading-none mt-1">Hip-hop · Break · Street art</div>
          </div>
          <div className="flex items-center gap-1 -mr-1 -mt-1">
            <button type="button" onClick={() => setExpanded(false)} data-testid="courses-widget-minimize" aria-label="Réduire" className="w-7 h-7 rounded-full grid place-items-center text-[color:var(--text-muted)] hover:text-white hover:bg-white/10 transition"><ChevronRight className="w-4 h-4" /></button>
            <button type="button" onClick={dismiss} data-testid="courses-widget-close" aria-label="Fermer" className="w-7 h-7 rounded-full grid place-items-center text-[color:var(--text-muted)] hover:text-white hover:bg-white/10 transition"><X className="w-4 h-4" /></button>
          </div>
        </div>
        {next && (
          <div className="mt-3 rounded-xl bg-[color:var(--bg)] border border-[color:var(--border)] px-3 py-2 text-xs flex items-center justify-between gap-2" data-testid="courses-widget-next">
            <span className="text-[color:var(--text-muted)] truncate">Prochaine séance · <span style={{ color: next.color }}>{next.course_name}</span> {next.day_label.toLowerCase()} {next.slot?.start}</span>
            <Countdown start={next.start} />
          </div>
        )}
        <PromoVideoWidget compact className="mt-3" />
        <div className="mt-3 flex items-center gap-2">
          <Link to="/cours#inscription" data-testid="courses-widget-enroll" className="flex-1 inline-flex items-center justify-center gap-2 bg-[color:var(--accent)] text-black font-semibold px-4 py-2.5 rounded-full text-[10px] uppercase tracking-widest hover:brightness-110 transition">Je réserve ma place</Link>
          <Link to="/cours" data-testid="courses-widget-more" className="inline-flex items-center gap-1 px-3 py-2.5 rounded-full border border-[color:var(--border)] hover:border-white text-[10px] uppercase tracking-widest transition-colors">Les cours</Link>
        </div>
      </div>
    </aside>
  );
};
